import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Settings, Edit, LogOut } from 'lucide-react';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

export const Profile = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-pink-50 p-6 pb-24" data-testid="profile-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-testid="profile-title">{t('myProfile')}</h1>
          <div className="flex gap-2">
            <LanguageSwitcher variant="outline" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/setup')}
              className="rounded-full"
              data-testid="edit-profile-button"
            >
              <Edit className="w-4 h-4 mr-2" />
              {t('edit')}
            </Button>
          </div>
        </div>

        <Card className="p-6 rounded-2xl mb-6">
          <div className="grid grid-cols-3 gap-4 mb-6">
            {user.photos?.map((photo, idx) => (
              <img
                key={idx}
                src={photo}
                alt={`Profile ${idx}`}
                className="aspect-square rounded-xl object-cover"
              />
            ))}
          </div>

          <div className="space-y-4">
            <div>
              <h2 className="text-2xl font-heading font-bold" data-testid="profile-display-name">{user.name}, {user.age}</h2>
              <p className="text-slate-600 mt-1">{user.email}</p>
            </div>

            {user.bio && (
              <div>
                <h3 className="font-semibold text-sm text-slate-700 mb-1">{t('about')}</h3>
                <p className="text-slate-600" data-testid="profile-display-bio">{user.bio}</p>
              </div>
            )}

            {user.interests?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-slate-700 mb-2">{t('interests')}</h3>
                <div className="flex flex-wrap gap-2">
                  {user.interests.map((interest, idx) => (
                    <span
                      key={idx}
                      className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium"
                    >
                      {interest}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-purple-100">
              <h3 className="font-semibold text-sm text-slate-700 mb-2">{t('stats')}</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-3">
                  <p className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">{user.swipe_count || 0}</p>
                  <p className="text-xs text-slate-600">{t('swipes')}</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-3">
                  <p className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">{user.super_likes || 3}</p>
                  <p className="text-xs text-slate-600">{t('superLikes')}</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-3">
                  <p className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">{user.boosts || 0}</p>
                  <p className="text-xs text-slate-600">{t('boosts')}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Button
          variant="destructive"
          onClick={handleLogout}
          className="w-full rounded-full"
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4 mr-2" />
          {t('logout')}
        </Button>
      </div>
    </div>
  );
};